import React, { useState } from 'react';
import { Building2, Users, Crown, Shield, UserCircle, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../../contexts/OrganizationContext';
import ErrorMessage from '../ErrorMessage';

const OrganizationsList: React.FC = () => {
  const navigate = useNavigate();
  const { organizations, currentOrganization, isLoading, switchOrganization } = useOrganization();
  const [error] = useState<unknown>(null);

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="h-4 w-4 text-yellow-600" />;
      case 'admin':
        return <Shield className="h-4 w-4 text-blue-600" />;
      default:
        return <UserCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-yellow-100 text-yellow-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Chargement des organisations...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Mes Organisations</h2>
            <p className="text-gray-600 mt-1">
              Gérez vos organisations et passez de l'une à l'autre
            </p>
          </div>
          <button
            onClick={() => navigate('/organizations/create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Nouvelle Organisation</span>
          </button>
      </div>

      {error && (
        <ErrorMessage error={error} className="mb-6" />
      )}

      {/* Organizations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {organizations.map((org) => {
          const isCurrent = currentOrganization?.id === org.id;
          
          return (
            <div
              key={org.id}
              className={`bg-white rounded-lg shadow-sm border-2 transition-all ${
                isCurrent 
                  ? 'border-blue-500 ring-2 ring-blue-100' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`p-3 rounded-lg ${
                      isCurrent ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <Building2 className={`h-6 w-6 ${
                        isCurrent ? 'text-blue-600' : 'text-gray-600'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{org.name}</h3>
                      {isCurrent && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                          Organisation active
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Role Badge */}
                <div className="flex items-center space-x-2 mb-4">
                  {getRoleIcon(org.role)}
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getRoleBadge(org.role)}`}>
                    {org.role === 'owner' ? 'Propriétaire' : 
                     org.role === 'admin' ? 'Administrateur' : 
                     'Membre'}
                  </span>
                </div>

                {/* Metadata */}
                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <div className="flex items-center space-x-2">
                    <Users className="h-3 w-3" />
                    <span>Membre depuis {new Date(org.joined_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                </div>

                {/* Actions */}
                {!isCurrent && (
                  <button
                    onClick={() => switchOrganization(org.id)}
                    className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Changer d'organisation
                  </button>
                )}

                {isCurrent && (
                  <div className="text-center text-sm text-gray-500">
                    Vous travaillez actuellement dans cette organisation
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {organizations.length === 0 && (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-gray-100 mb-4">
            <Building2 className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucune organisation
          </h3>
          <p className="text-gray-600 mb-6">
            Créez votre première organisation pour commencer
          </p>
          <button
            onClick={() => navigate('/organizations/create')}
            className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            <span>Créer une organisation</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default OrganizationsList;

